// src/features/equipment/pages/CreateEquipmentPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Components
import EquipmentForm from '../components/EquipmentForm';

// Hooks
import { useEquipment } from '../hooks/useEquipment';
import { useRooms } from '../../rooms/hooks/useRooms';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useAuth } from '../../../context/AuthContext';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { FormPage, FormActions } from '../../../components/common/form';

export default function CreateEquipmentPage() {
  const { t } = useTranslation(['equipment', 'rooms', 'common']);
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { showSuccess, showError } = useSnackbar();
  const { user } = useAuth();
  const { navigateBack } = useNavigationStack();

  const location = useLocation();
  const templateData = location.state?.template;
  // Set by flows like onboarding that want the user sent back after creating
  const returnTo = location.state?.returnTo;

  // State
  const [room, setRoom] = useState(null);
  const [formData, setFormData] = useState({
    name: templateData?.name || '',
    equipment_type: templateData?.equipment_type || '',
    manufacturer: templateData?.manufacturer || '',
    model: templateData?.model || '',
    serial_number: '',
    batch_number: '',
    max_voltage_kv: templateData?.max_voltage_kv || '',
    last_receipt_control_date: '',
    notes: templateData?.notes || '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [loadingRoom, setLoadingRoom] = useState(true);

  // API hooks
  const {
    createEquipment,
    loading: creating,
    error: equipmentError,
    clearError: clearEquipmentError,
  } = useEquipment();

  const {
    fetchRoomById,
    error: roomError,
  } = useRooms();

  // Load room data
  useEffect(() => {
    const loadRoomData = async () => {
      if (!roomId || roomId === "undefined") {
        // No room ID provided, just finish loading
        setLoadingRoom(false);
        return;
      }

      try {
        setLoadingRoom(true);
        const result = await fetchRoomById(roomId);
        
        if (result.success) {
          setRoom(result.data);
        } else {
          // Room not found, but continue anyway
          setRoom(null);
        }
      } catch (error) {
        // Error loading room, but continue anyway
        setRoom(null);
      } finally {
        setLoadingRoom(false);
      }
    };

    loadRoomData();
  }, [roomId, fetchRoomById]);

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('equipment:validation.name_required');
    } else if (formData.name.length < 2) {
      errors.name = t('equipment:validation.name_min_length');
    } else if (formData.name.length > 255) {
      errors.name = t('equipment:validation.name_max_length');
    }

    if (!formData.equipment_type) {
      errors.equipment_type = t('equipment:validation.type_required');
    }

    if (formData.max_voltage_kv && parseFloat(formData.max_voltage_kv) <= 0) {
      errors.max_voltage_kv = t('equipment:validation.max_voltage_positive');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!user?.department?.department_id) {
      showError(t('equipment:errors.no_department'));
      return;
    }

    try {
      const equipmentData = {
        room_id: room?.room_id || null,
        department_id: user.department.department_id,
        equipment_type: formData.equipment_type,
        name: formData.name.trim(),
        manufacturer: formData.manufacturer.trim() || null,
        model: formData.model.trim() || null,
        serial_number: formData.serial_number.trim() || null,
        batch_number: formData.batch_number.trim() || null,
        max_voltage_kv: formData.max_voltage_kv ? parseFloat(formData.max_voltage_kv) : null,
        last_receipt_control_date: formData.last_receipt_control_date || null,
        notes: formData.notes.trim() || null,
      };

      const result = await createEquipment(equipmentData);

      if (result.success) {
        showSuccess(t('equipment:success.equipment_created'));
        if (returnTo) {
          navigate(returnTo);
        } else {
          navigateBack(room ? `/rooms/${room.room_id}` : '/equipment');
        }
      } else {
        showError(result.error || t('equipment:errors.create_failed'));
      }
    } catch (error) {
      showError(t('equipment:errors.unexpected_error'));
    }
  }, [validateForm, formData, user, room, createEquipment, showSuccess, showError, navigate, navigateBack, returnTo, t]);

  const handleCancel = useCallback(() => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigateBack(room ? `/rooms/${room.room_id}` : '/rooms');
    }
  }, [navigate, navigateBack, returnTo, room]);

  // Loading state
  if (loadingRoom) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormPage
      title={t('equipment:add_equipment')}
      subtitle={room ? t('equipment:form.create_subtitle') : t('equipment:form.create_without_room')}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={equipmentError || roomError}
      onErrorClose={clearEquipmentError}
    >
      {!room && roomId && (
        <Alert severity="warning">
          {t('equipment:warnings.no_room_found')}
        </Alert>
      )}
      <EquipmentForm
        formData={formData}
        onInputChange={handleInputChange}
        validationErrors={validationErrors}
        loading={creating}
        mode="create"
        showRoomInfo={!!room}
        roomName={room?.name}
      />
      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={creating ? t('equipment:actions.creating') : t('equipment:actions.create_equipment')}
        submitIcon={<SaveIcon />}
        loading={creating}
      />
    </FormPage>
  );
}
